/**
 * Traffic source (localStorage `source`)
 *
 * - Abandoned-cart direct hit (page opened on a /checkout/ case URL this
 *   browser did not walk through): source comes only from the funnel-get-data
 *   response for the rest of the browser session; ?affid= never changes it.
 * - Otherwise:
 *   - `?affid=56` → source = "meta"; any other affid clears it.
 *   - funnel-get-data overwrites it only when this browser session did not
 *     land with an `?affid=` — a returning visitor keeps their old case, whose
 *     stored source may be stale.
 * - Read by Meta tracking (sent only when source = "meta") and the funnel log.
 */

import { localStore } from './localStorageKey'
import { sessionStore } from './sessionKey'
import { isDirectCheckoutSelectionHit } from './everflow'
import { logger } from './logger'

const SOURCE_KEY = 'source'
// sessionStorage: URL affid seen in this browser session. Session-scoped so a
// new visit always re-applies its own ?affid=.
const SESSION_URL_AFFID_KEY = 'source_url_affid'
// sessionStorage: this browser session started as an abandoned-cart direct
// hit. Session-scoped because the Stripe 3DS / BNPL return is a new page load
// that is no longer a direct hit, but source must still come from the API.
const SESSION_DIRECT_HIT_KEY = 'source_direct_hit'
// localStorage key written by an earlier version of this module.
const LEGACY_SOURCE_AFFID_KEY = 'source_affid'

export const META_SOURCE = 'meta'
const META_SOURCE_AFFID = '56'

const getUrlAffid = (search = typeof window !== 'undefined' ? window.location.search : '') => {
  try {
    const value = new URLSearchParams(search).get('affid')
    return value === null ? null : String(value).trim()
  } catch (_) {
    return null
  }
}

let directHitPageLoadStarted = false

const isDirectHitSession = () => {
  if (isDirectCheckoutSelectionHit()) {
    sessionStore.setItem(SESSION_DIRECT_HIT_KEY, 'true')
    if (!directHitPageLoadStarted) {
      directHitPageLoadStarted = true
      // A source left from an earlier visit must not drive Meta before
      // funnel-get-data answers for this case.
      localStore.removeItem(SOURCE_KEY)
      logger.debug('🏷️ Abandoned-cart direct hit — source cleared until funnel-get-data responds')
    }
    return true
  }
  return sessionStore.getItem(SESSION_DIRECT_HIT_KEY) === 'true'
}

/** @returns {string|null} */
export const getStoredSource = () => localStore.getItem(SOURCE_KEY) || null

export const isMetaSource = () => getStoredSource() === META_SOURCE

const writeSource = (source) => {
  if (source) {
    localStore.setItem(SOURCE_KEY, source)
  } else {
    localStore.removeItem(SOURCE_KEY)
  }
}

/**
 * Set `source` from `?affid=`. No-op when the URL has no affid, or in an
 * abandoned-cart direct-hit session (source comes only from funnel-get-data).
 * @param {string} [search] - location.search
 */
export const syncSourceFromUrl = (search = typeof window !== 'undefined' ? window.location.search : '') => {
  const affid = getUrlAffid(search)

  if (isDirectHitSession()) {
    if (affid !== null) logger.debug('⏭️ Direct-hit session — ignoring URL affid for source:', affid)
    return
  }

  if (affid === null) return

  const source = affid === META_SOURCE_AFFID ? META_SOURCE : null
  const changed = sessionStore.getItem(SESSION_URL_AFFID_KEY) !== affid || getStoredSource() !== source

  writeSource(source)
  sessionStore.setItem(SESSION_URL_AFFID_KEY, affid)
  localStore.removeItem(LEGACY_SOURCE_AFFID_KEY)
  if (changed) logger.debug('🏷️ source set from URL affid:', { affid, source })
}

/**
 * Overwrite `source` from a funnel-get-data response. Only acts when the
 * response carries a `source` field (null/empty clears it). In an
 * abandoned-cart direct-hit session it always applies; otherwise only when
 * this session did not land with an `?affid=`.
 * @param {Object} result - Parsed funnel-get-data JSON
 */
export const applySourceFromFunnelGetData = (result) => {
  const sessionUrlAffid = sessionStore.getItem(SESSION_URL_AFFID_KEY)
  const isAbandonedCartDirectHit = isDirectHitSession()
  if (!isAbandonedCartDirectHit && sessionUrlAffid !== null) {
    logger.debug('⏭️ Keeping source from this session\'s URL affid; ignoring funnel-get-data source:', sessionUrlAffid)
    return
  }

  const data = result?.data
  const funnelFields = data?.data
  let source
  if (funnelFields && Object.prototype.hasOwnProperty.call(funnelFields, SOURCE_KEY)) {
    source = funnelFields[SOURCE_KEY]
  } else if (data && Object.prototype.hasOwnProperty.call(data, SOURCE_KEY)) {
    source = data[SOURCE_KEY]
  } else {
    return
  }

  const normalized = source ? String(source).trim() : ''
  writeSource(normalized)
  logger.debug('🏷️ source set from funnel-get-data:', {
    source: normalized || null,
    abandonedCartDirectHit: isAbandonedCartDirectHit,
  })
}

export default {
  getStoredSource,
  isMetaSource,
  syncSourceFromUrl,
  applySourceFromFunnelGetData,
}
